import {
  IntegrationError,
  IntegrationHttpError,
  IntegrationTimeoutError,
} from '@rammetec/data-access'

export interface ApiErrorMessageOptions {
  /** Per-HTTP-status pt-BR overrides (e.g. `{ 409: 'Já existe...' }`). */
  byStatus?: Record<number, string>
  /** Message used when nothing more specific is available. */
  fallback?: string
}

/**
 * Maps an integration/HTTP error into a user-facing (pt-BR) message for toasts.
 *
 * Precedence: per-status override → API response body `message` → HTTP status →
 * the error message → the generic fallback. Use in a module's `onSave`/`onDelete`
 * catch to surface why a System API call failed (CrudGrid does not display
 * save-time errors itself).
 */
export function apiErrorMessage(error: unknown, opts: ApiErrorMessageOptions = {}): string {
  const fallback = opts.fallback ?? 'Ocorreu um erro. Tente novamente.'

  if (error instanceof IntegrationTimeoutError) {
    return 'O servidor demorou para responder. Tente novamente.'
  }
  if (error instanceof IntegrationHttpError) {
    const status = error.statusCode
    if (status != null && opts.byStatus?.[status]) return opts.byStatus[status]
    const body = error.responseBody as { message?: unknown } | undefined
    if (typeof body?.message === 'string' && body.message.trim()) return body.message
    return status != null ? `Falha na operação (HTTP ${status}).` : fallback
  }
  if (error instanceof IntegrationError) {
    return error.message || fallback
  }
  if (error instanceof Error) {
    return error.message || fallback
  }
  return fallback
}
